'use client';

import { useTranslations } from 'next-intl';
import { Fail } from '@/app/components/icons/Fail';
import { Loading } from '@/app/components/icons/Loading';
import { useVoiceClonePendingSse } from '../_hooks/use-voice-clone-pending-sse';
import type { MaterialV1Voice, VoiceCloneJobItem } from '../types';
import { VoiceInfoEditor } from './VoiceInfoEditor';

interface VoiceCloneJobCardProps {
  job: VoiceCloneJobItem;
  onVoiceNameChange: (voiceId: string, voiceName: string) => void;
  onVoiceDescriptionChange: (voiceId: string, voiceDescription: string) => void;
  onVoiceSucceeded: (voice: MaterialV1Voice) => void;
  onVoiceProcessing: (payload: { voiceId: string }) => void;
  onVoiceFailed: (payload: { voiceId: string }) => void;
}

export function VoiceCloneJobCard({
  job,
  onVoiceNameChange,
  onVoiceDescriptionChange,
  onVoiceSucceeded,
  onVoiceProcessing,
  onVoiceFailed,
}: VoiceCloneJobCardProps) {
  const t = useTranslations('tools.voiceClone.jobCard');
  const isPending = job.cardStatus === 'queued' || job.cardStatus === 'processing';

  useVoiceClonePendingSse({
    voiceId: job.voiceId,
    enabled: isPending,
    onVoiceSucceeded,
    onVoiceProcessing,
    onVoiceFailed,
  });

  return (
    <div className="flex flex-col gap-4">
      {isPending && (
        <div className="flex h-[58px] items-center gap-3 rounded-16 border border-system-white08 bg-system-white04 px-4 backdrop-blur-[30px]">
          <Loading className="size-5 shrink-0 animate-spin text-system-white/72" aria-hidden />
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-semibold text-sm text-system-white">
              {job.cardStatus === 'queued' ? t('statusQueued') : t('statusProcessing')}
            </span>
            <span className="truncate text-system-white/48 text-xs">{job.voiceName}</span>
          </div>
        </div>
      )}

      {job.cardStatus === 'failed' && (
        <div className="flex h-[58px] items-center gap-3 rounded-16 border border-system-white08 bg-system-white04 px-4 backdrop-blur-[30px]">
          <Fail className="size-5 shrink-0 text-red-400" aria-hidden />
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-semibold text-red-400 text-sm">{t('statusFailed')}</span>
            <span className="text-system-white/48 text-xs leading-[1.4]">{t('failedHint')}</span>
          </div>
        </div>
      )}

      {job.cardStatus === 'succeeded' && (
        <VoiceInfoEditor
          voiceId={job.voiceId}
          audio={job.audio}
          voiceName={job.voiceName}
          voiceDescription={job.voiceDescription}
          onVoiceNameChange={(value) => onVoiceNameChange(job.voiceId, value)}
          onVoiceDescriptionChange={(value) => onVoiceDescriptionChange(job.voiceId, value)}
        />
      )}
    </div>
  );
}
